import loadable from '@loadable/component';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import PageBuilderContext, {
  IPageBuilderContext,
} from '../contexts/page-builder/page-builder-context';
import PageContext from '../contexts/page/page-context';
import { get } from 'lodash';
import {
  ComparatorContext,
  useWizardService,
  useCurrentStepIndex,
  useSteps,
} from '../contexts';
import { useLocation } from 'react-router-dom';
import { HookTypes, Module, ModuleDef, ModuleFn } from '../config/modules';
export interface PageLayout {
  name: string;
  components: ComponentDescriptor[];
  localState?: { [key: string]: unknown };
}

export type ComponentDescriptor<P = any> = {
  type: string;
  props?: P;
  _children?: ComponentDescriptor[];
};

export interface BasePageProps extends PageLayout {}
export type PageProps = React.PropsWithChildren<BasePageProps>;

const initModules = (
  pageName: string,
  { pageBuilder, comparator, wizard }: Parameters<ModuleFn>[0]
) => {
  for (const moduleDef of comparator.modules) {
    if (
      typeof moduleDef === 'object' &&
      moduleDef.hasOwnProperty('on') &&
      moduleDef.on?.type === HookTypes.PAGE_LOADED &&
      moduleDef.on.meta.pageName === pageName
    ) {
      moduleDef.module({ pageBuilder, comparator, wizard });
    }
  }
};

const isModuleDef = (module: Module): module is ModuleDef => {
  return typeof module === 'object' && module.hasOwnProperty('waitUntilLoaded');
};

const usePageChangeEffects = ({ name }: Pick<PageProps, 'name'>) => {
  const location = useLocation();
  const currentStepIndex = useCurrentStepIndex();
  const steps = useSteps() ?? [];
  const wizard = useWizardService();
  const comparator = useContext(ComparatorContext);
  const pageBuilder = useContext(PageBuilderContext);

  useEffect(() => {
    if (
      comparator.allModulesLoaded === false &&
      comparator.modules.filter(
        (m) => isModuleDef(m) && m.waitUntilLoaded === true
      ).length === [...new Set(comparator.loadedModules)].length
    )
      comparator.setAllModulesLoaded(true);
  }, [comparator.loadedModules]);

  useEffect(() => {
    if (comparator.allModulesLoaded) {
      const correspondingIndexInConfig =
        steps?.findIndex((s) => s.name === name) ?? -1;
      // it gets out of sync if the navigation occurred thru the back and forward buttons of the browser
      // it became the case when switching to an SPA (navigating thru react router v6 vs location.assign)
      const isOutOfSync =
        correspondingIndexInConfig > -1 &&
        correspondingIndexInConfig !== currentStepIndex;
      if (isOutOfSync) {
        wizard?.send({
          type: 'GOTO',
          index: correspondingIndexInConfig,
          meta: { navigate: false },
        });
      }
    }
  }, [comparator.allModulesLoaded, location]);

  useEffect(() => {
    initModules(name, { pageBuilder, comparator, wizard });
  }, [location]);

  useEffect(() => {
    if (location.search !== '') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [location]);

  return comparator;
};

export const Page: React.FC<PageProps> = ({
  children,
  name,
  components,
  localState,
}) => {
  const [state, setState] = useState(localState);

  const comparator = usePageChangeEffects({ name });

  const pageContext = {
    name,
    components,
    state,
    setState,
  };
  return comparator.allModulesLoaded ? (
    <PageContext.Provider value={pageContext}>{children}</PageContext.Provider>
  ) : null;
};

export interface ComponentRendererProps {
  serializedComponent: ComponentDescriptor;
}

export const loadComponent = (
  type: string,
  loadFn: (() => Promise<any>) | undefined
) => {
  return !loadFn
    ? null
    : loadable<any, any>(loadFn, {
        resolveComponent: (components) => {
          const component = get(components, type);
          if (!component)
            console.error('Component not found:', type, 'in ', components);
          return component;
        },
      });
};

export const useLoadableComponent = (
  type: string,
  loadFn: (() => Promise<any>) | undefined
) => {
  return useMemo(() => loadComponent(type, loadFn), [loadFn, type]);
};

export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  serializedComponent,
}) => {
  const { loadFn } = useContext<IPageBuilderContext>(PageBuilderContext);
  const { props = {}, type, _children } = serializedComponent;
  const children = useMemo(() => {
    if (typeof props === 'object' && props.children) return props.children;
    if (!_children) {
      return undefined;
    } else {
      return _children.map((c, i) => (
        <ComponentRenderer key={i} serializedComponent={c} />
      ));
    }
  }, [_children, props.children]);
  const Component = useLoadableComponent(type, loadFn);

  return useMemo(() => {
    return Component ? <Component {...props}>{children}</Component> : null;
  }, [Component, props, children]);
};
